// ============================================================================
// Bases y Propiedades — amenidades y reservas (store compartido)
// ============================================================================
// Fuente única para Amenidades, Reservas y sus tableros. Desde CC-CONSOLE #1592
// lee y escribe por la API (`amenities`, `amenity_bookings`, por compañía en el
// servidor); ya no hay localStorage ni datos de ejemplo en el navegador. Los
// datos de ejemplo salen del Sandbox.
// ============================================================================
import { create } from "zustand";
import * as amenitiesApi from "./services/amenitiesApi";

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

/** Pricing model: per booking, per hour, or per block. */
export type PriceUnit = "por_reserva" | "por_hora" | "por_bloque";

/** A reservable space: meeting room, gym studio, BBQ/asador, Airbnb-style room… */
export interface Amenity {
  id: string;
  name: string;
  /** sala-de-juntas | salon | cancha | asador | habitacion | alberca | otro */
  type: string;
  description: string;
  /** Ubicación / base donde se encuentra la amenidad. */
  location?: string;
  /** Optional price. 0 → free (no payment required). */
  price: number;
  /** How `price` is charged. Defaults to "por_reserva". */
  priceUnit?: PriceUnit;
  currency: string;
  capacity: number;
  /** Minimum / maximum reservable duration in minutes. */
  minMinutes: number;
  maxMinutes: number;
  /** Booking window the same day (HH:mm). */
  openTime: string;
  closeTime: string;
  /** Available weekdays (lun, mar, mie, jue, vie, sab, dom). Empty → all days. */
  days?: string[];
  /** Maximum booking anticipation (days). */
  maxAdvanceDays?: number;
  /** Buffer between reservations (minutes). */
  bufferMinutes?: number;
  /** Free-text usage rules shown to the booker / on the public page. */
  rules: string;
  /** Cancellation policy. */
  cancellationPolicy?: string;
  /** Required deposit (0 → none). */
  deposit?: number;
  /** Access requirements / prerequisites. */
  requirements?: string;
  /** Photo URLs (remote Storage URLs or local data URLs). First is the cover. */
  photos: string[];
  status: "Disponible" | "Mantenimiento" | "Oculta";
  /** Whether the amenity is shown on the public page. */
  visiblePublic?: boolean;
  /** Base (property_bases) where the amenity lives. */
  base_id?: string | null;
}

export type BookingChannel = "staff" | "public";
export type BookingStatus = "Por confirmar" | "Confirmada" | "Cancelada";
export type PaymentStatus = "No requerido" | "Pendiente" | "Pagado";

/** A reservation against an amenity (the "when/who").
 * These fields are the contract a Firestore-backed `bookings` collection
 * (scoped by companyId) fills later: amenity, customer, agent (if any),
 * date/time + duration, status, price and the payment/invoice references. */
export interface Booking {
  id: string;
  amenityId: string;
  amenityName: string;
  customerName: string;
  customerEmail: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  /** Reservation length in minutes (endTime − startTime). */
  durationMinutes?: number;
  channel: BookingChannel;
  /** Agent who created a staff booking on behalf of the customer (if any). */
  agentId?: string;
  agentName?: string;
  status: BookingStatus;
  price: number;
  paymentStatus: PaymentStatus;
  /** Payment provider reference once a charge is captured (Stripe, etc.). */
  paymentRef?: string;
  /** Invoice number once the purchase/invoice is triggered (FAC-YYYY-#####). */
  invoiceNumber?: string;
  notes?: string;
  /** Base of the amenity (set by the server). */
  base_id?: string | null;
}

// ---------------------------------------------------------------------------
// Store (API)
// ---------------------------------------------------------------------------

export type LoadState = "idle" | "loading" | "ready" | "error";

interface BasesState {
  amenities: Amenity[];
  bookings: Booking[];
  /** El rango por defecto (−90 a +180 días) tenía más reservas de las que se trajeron. */
  bookingsHasMore: boolean;
  bookingsRange: { from?: string; to?: string };
  status: LoadState;
  error: string | null;
  /** Lee amenidades y reservas de la compañía (llamar al montar). */
  hydrate: () => Promise<void>;
  addAmenity: (a: Omit<Amenity, "id">, newFiles?: File[]) => Promise<void>;
  updateAmenity: (id: string, patch: Omit<Amenity, "id">, newFiles?: File[]) => Promise<void>;
  removeAmenity: (id: string) => Promise<void>;
  /** Crea la reserva; con `issueInvoice` el servidor asigna el folio FAC-AAAA-#####. */
  addBooking: (b: Omit<Booking, "id">, issueInvoice?: boolean) => Promise<Booking>;
  updateBooking: (id: string, patch: Partial<Booking>, issueInvoice?: boolean) => Promise<Booking>;
  removeBooking: (id: string) => Promise<void>;
}

const errorText = (err: unknown) => {
  const e = err as { response?: { data?: { meta?: { message?: string } } }; message?: string };
  return e?.response?.data?.meta?.message || e?.message || "error";
};

export const useBasesStore = create<BasesState>((set, get) => ({
  amenities: [],
  bookings: [],
  bookingsHasMore: false,
  bookingsRange: {},
  status: "idle",
  error: null,

  hydrate: async () => {
    set({ status: get().status === "ready" ? "ready" : "loading", error: null });
    try {
      const [amenities, page] = await Promise.all([amenitiesApi.listAmenities(), amenitiesApi.listBookings()]);
      set({ amenities, bookings: page.items, bookingsHasMore: page.hasMore, bookingsRange: { from: page.from, to: page.to }, status: "ready" });
    } catch (err) {
      set({ status: "error", error: errorText(err) });
    }
  },

  addAmenity: async (a, newFiles = []) => {
    const created = await amenitiesApi.createAmenity(a, newFiles);
    set({ amenities: [...get().amenities, created] });
  },

  updateAmenity: async (id, patch, newFiles = []) => {
    const saved = await amenitiesApi.updateAmenity(id, patch, newFiles);
    set({ amenities: get().amenities.map((a) => (a.id === id ? saved : a)) });
  },

  removeAmenity: async (id) => {
    await amenitiesApi.deleteAmenity(id);
    set({ amenities: get().amenities.filter((a) => a.id !== id) });
  },

  addBooking: async (b, issueInvoice = false) => {
    const created = await amenitiesApi.createBooking(b, issueInvoice);
    set({ bookings: [created, ...get().bookings] });
    return created;
  },

  updateBooking: async (id, patch, issueInvoice = false) => {
    const saved = await amenitiesApi.updateBooking(id, patch, issueInvoice);
    set({ bookings: get().bookings.map((b) => (b.id === id ? saved : b)) });
    return saved;
  },

  removeBooking: async (id) => {
    await amenitiesApi.deleteBooking(id);
    set({ bookings: get().bookings.filter((b) => b.id !== id) });
  },
}));

/**
 * Amenity type keys. Human labels are translated in the UI via i18n
 * (`basesPropiedades.amenityTypes.<key>`), so this list stays language-agnostic.
 */
export const AMENITY_TYPES = [
  "sala-de-juntas",
  "salon",
  "comedor",
  "anden",
  "cajon",
  "cancha",
  "asador",
  "habitacion",
  "alberca",
  "otro",
] as const;
